import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface ManagerAgent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  pending_count: number;
  /** Total active clients assigned to this agent (book size). Added for the
   *  manager Team roster. Optional so older callers still typecheck. */
  client_count?: number;
}

export interface ReassignAgentResponse {
  client_id: string;
  old_agent_id: string | null;
  /** null when the client was returned to the pool (no assigned agent). */
  new_agent_id: string | null;
  reassigned_active_cases: number;
  correlation_id: string;
}

export interface ClientSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export type ApprovalType =
  | 'deposit'
  | 'withdrawal'
  | 'withdrawal_request'
  | 'contract'
  | 'onboarding'
  | 'escalated_callback'
  | 'unassigned_callback';

export interface ApprovalQueueItem {
  id: string;
  type: ApprovalType;
  client_id: string;
  client_name: string;
  client_email: string;
  context: string;
  rejection_reason: string | null;
  agent_id: string | null;
  agent_name: string | null;
  created_at: string;
  review_url: string;
  /** Past the 48h manager-response SLA. Backend-computed so it can't drift
   *  from the sla-summary breach count. Only onboarding/contract rows can
   *  be flagged; deposits and callbacks are always false. */
  sla_breached: boolean;
}

export interface TimeOffBlockSummary {
  starts_at: string;
  ends_at: string;
  reason: string | null;
}

export interface TeamCapacityItem {
  agent_id: string;
  agent_first_name: string;
  agent_last_name: string;
  agent_email: string;
  active_case_count: number;
  overdue_case_count: number;
  is_on_time_off: boolean;
  upcoming_time_off: TimeOffBlockSummary[];
}

export interface SlaSummary {
  sla_breach_count: number;
  kyc_expiring_count: number;
  security_incidents_count: number;
}

class ManagerService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listAgents(): Promise<ManagerAgent[]> {
    const res = await this.api.get<{ agents: ManagerAgent[] }>('/api/v1/manager/agents');
    return res.data.agents;
  }

  /** Every active client visible to a manager / super_admin. Agents
   *  must use `agentService.listMyClients()` to get their own roster
   *  — the two scopes are split so the URL no longer lies about what
   *  it returns. */
  async listClients(): Promise<ClientSummary[]> {
    const res = await this.api.get<{ items: ClientSummary[] }>('/api/v1/manager/clients');
    return res.data.items;
  }

  /** Active clients with no primary agent (the pool). These return here when
   *  an agent is deactivated without a successor; reassign to drain it. */
  async listUnassignedClients(): Promise<ClientSummary[]> {
    const res = await this.api.get<{ items: ClientSummary[] }>(
      '/api/v1/manager/clients/unassigned',
    );
    return res.data.items;
  }

  async reassignClientAgent(
    clientId: string,
    // new_agent_id: null ⇒ return the client to the unassigned pool.
    body: { new_agent_id: string | null; reason: string },
  ): Promise<ReassignAgentResponse> {
    const res = await this.api.patch<ReassignAgentResponse>(
      `/api/v1/manager/clients/${clientId}/reassign-agent`,
      body,
    );
    return res.data;
  }

  async getApprovalsQueue(): Promise<ApprovalQueueItem[]> {
    const res = await this.api.get<{ items: ApprovalQueueItem[]; count: number }>(
      '/api/v1/manager/approvals-queue',
    );
    return res.data.items;
  }

  async getTeamCapacity(): Promise<TeamCapacityItem[]> {
    const res = await this.api.get<{ agents: TeamCapacityItem[] }>(
      '/api/v1/manager/team-capacity',
    );
    return res.data.agents;
  }

  async getSlaSummary(): Promise<SlaSummary> {
    const res = await this.api.get<SlaSummary>('/api/v1/manager/sla-summary');
    return res.data;
  }
}

export default new ManagerService();
